if (!customElements.get('list-card-product')) {
  // Page-level registry of swatch product data, keyed by handle. Each card's
  // Liquid now embeds ONLY its own product entry (siblings used to be
  // re-serialized into every group member's card — ~50% of the payload was
  // exact duplicates). Cards share sibling data through this registry; a
  // sibling whose card isn't on the page is fetched on demand.
  window.__mnSwatchProductData = window.__mnSwatchProductData || {};
  window.__mnSwatchProductFetches = window.__mnSwatchProductFetches || {};
  // set_types/drops_in for siblings whose own card isn't on the page, emitted by
  // card-product.liquid ([data-swatch-metafield-data]). /products/{handle}.js
  // cannot return metafields, so the network path merges these in.
  window.__mnSwatchMetafieldData = window.__mnSwatchMetafieldData || {};

  // Merge every not-yet-seen swatch blob on the page into the registries.
  // Cheap to call repeatedly — parsed scripts are marked.
  function collectSwatchProductData() {
    const merge = (selector, target) => {
      document.querySelectorAll(selector).forEach((script) => {
        if (script.dataset.swatchCollected) return;
        script.dataset.swatchCollected = 'true';
        try {
          Object.assign(target, JSON.parse(script.textContent));
        } catch (e) {
          console.warn('Failed to parse embedded swatch data:', e);
        }
      });
    };
    merge('script[data-swatch-product-data]', window.__mnSwatchProductData);
    merge('script[data-swatch-metafield-data]', window.__mnSwatchMetafieldData);
  }

  // /products/{handle}.js mapped to the same shape as the Liquid-embedded
  // Storefront-style data. The .js endpoint carries no metafields, so set_types
  // and dropsIn come from the sibling metafield registry above.
  function mapProductJsToSwatchData(p) {
    const img = (url) => url ? { url: url.indexOf('?') === -1 ? url + '?width=800' : url, altText: p.title || '', width: 0, height: 0 } : null;
    return {
      id: 'gid://shopify/Product/' + p.id,
      title: p.title,
      handle: p.handle,
      availableForSale: !!p.available,
      tags: p.tags || [],
      productType: p.type || '',
      featuredImage: img(p.featured_image),
      images: { edges: (p.images || []).slice(0, 2).map((u) => ({ node: img(u) })) },
      options: (p.options || []).map((o) =>
        typeof o === 'string' ? { name: o, values: [] } : { name: o.name, values: o.values || [] }
      ),
      variants: {
        edges: (p.variants || []).map((v) => ({
          node: {
            id: 'gid://shopify/ProductVariant/' + v.id,
            title: v.title,
            option1: v.option1,
            option2: v.option2,
            option3: v.option3,
            availableForSale: !!v.available,
            price: { amount: String(v.price / 100) },
            compareAtPrice: v.compare_at_price ? { amount: String(v.compare_at_price / 100) } : null,
          },
        })),
      },
      set_types: (window.__mnSwatchMetafieldData[p.handle] || {}).set_types || null,
      dropsIn: (window.__mnSwatchMetafieldData[p.handle] || {}).dropsIn || null,
    };
  }

  // Resolve a handle's swatch data: registry -> fresh page scan -> network.
  // Single-flight per handle; resolves null on failure.
  function ensureSwatchProductData(handle) {
    if (window.__mnSwatchProductData[handle]) {
      return Promise.resolve(window.__mnSwatchProductData[handle]);
    }
    collectSwatchProductData();
    if (window.__mnSwatchProductData[handle]) {
      return Promise.resolve(window.__mnSwatchProductData[handle]);
    }
    if (!window.__mnSwatchProductFetches[handle]) {
      window.__mnSwatchProductFetches[handle] = fetch('/products/' + handle + '.js')
        .then((r) => {
          // Throttling/5xx must not poison the handle for the rest of the
          // session — the pointerenter warm-fetch can burst enough requests to
          // trip a 429, and a cached null makes the swatch look selected while
          // the card silently never updates. 404 is the one permanent answer.
          if (!r.ok) {
            if (r.status !== 404) delete window.__mnSwatchProductFetches[handle];
            return null;
          }
          return r.json();
        })
        .then((p) => {
          if (!p) return null;
          const data = mapProductJsToSwatchData(p);
          window.__mnSwatchProductData[handle] = data;
          return data;
        })
        .catch(() => {
          delete window.__mnSwatchProductFetches[handle];
          return null;
        });
    }
    return window.__mnSwatchProductFetches[handle];
  }

  customElements.define('list-card-product', class ListCardProduct extends HTMLElement {
    constructor() {
      super();

      this.productVariant = false;
      this.cartNotification = document.querySelector("cart-notification");
      this.mql = window.matchMedia('(min-width: 768px)');
      this.eventMouseEnterHappened = false;

      this._onAddToCart = this.handleAddToCart.bind(this);
      this._onWishlistClick = this.handleWishlistClick.bind(this);
      this._onOverloadOptionClick = this.handleOverloadOptionClick.bind(this);

      this.bindHostEvents();
      this.bindChildEvents();
      this.addAccessibilityAttributes();
      this.initializeVariantHandling();
      this.initializeWishlistHandling();
      this.initializeWishlistButtonHandling();
      this.initializeWishlistState();
      this.initializeSwatches();

      this.onClickAwayHandler = this.onClickAway.bind(this);
      this.onMediaQueryChangeHandler = this.onMediaQueryChange.bind(this);
    }

    connectedCallback() {
      document.addEventListener("click", this.onClickAwayHandler);
      this.mql.addEventListener('change', this.onMediaQueryChangeHandler);

      // Listen for wishlist updates from modal
      this.wishlistItemAddedHandler = (event) => {
        const { productId, variantId } = event.detail;
        // Check if this is our product and update state
        const ourVariantButtons = this.querySelectorAll('.backup-addToWishlist');
        ourVariantButtons.forEach(btn => {
          if (btn.dataset.productId == productId && btn.dataset.variantId == variantId) {
            this.fetchAndUpdateWishlistState();
          }
        });
      };
      document.addEventListener('wishlistItemAdded', this.wishlistItemAddedHandler);

      // Re-initialize handlers when connected to ensure proper setup
      this.initializeVariantHandling();
      this.initializeWishlistHandling();
      this.initializeWishlistButtonHandling();
      this.initializeWishlistState();
      this.initializeSwatches();
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.onClickAwayHandler);
      this.mql.removeEventListener('change', this.onMediaQueryChangeHandler);

      // Remove wishlist update listener
      if (this.wishlistItemAddedHandler) {
        document.removeEventListener('wishlistItemAdded', this.wishlistItemAddedHandler);
      }

      // Clean up any remaining event listeners
      this.cleanupEventListeners();

      // Clean up swatch resize observer
      if (this._swatchResizeObserver) {
        this._swatchResizeObserver.disconnect();
        this._swatchResizeObserver = null;
      }
    }

    onMediaQueryChange() {
      // Re-initialize handlers when media query changes
      this.initializeVariantHandling();
      this.initializeWishlistHandling();
      this.initializeWishlistButtonHandling();
    }

    cleanupEventListeners() {
      // Remove any dynamic event listeners to prevent memory leaks
      const modals = this.querySelectorAll('.cartModal, .wishModal');
      modals.forEach(modal => {
        modal.classList.remove('open');
      });

      if (document.body.classList.contains('overflow-hidden')) {
        document.body.classList.remove('overflow-hidden');
      }
    }

    // Host-level listeners (click on `this`) must be bound exactly once: `this.innerHTML =`
    // in loadProduct() replaces children but never the host, so re-running this would stack
    // a fresh .bind(this) closure on every variant re-render — N re-renders means N+1
    // add-to-cart fires per click, since none of the earlier closures can be removed.
    bindHostEvents() {
      if (this._hostEventsBound) return;
      this._hostEventsBound = true;

      this.addEventListener('click', this._onAddToCart);
      this.addEventListener('click', this._onWishlistClick);
      this.addEventListener('click', this._onOverloadOptionClick);
    }

    // Child-level listeners are attached to nodes that get fully replaced by
    // `this.innerHTML =` in loadProduct(), so re-running this on every re-render is required
    // (and safe — the old nodes, and their listeners, are gone).
    bindChildEvents() {
      this.querySelectorAll(".list-card-product__listbox a").forEach((anchor) => {
        anchor.addEventListener("click", this.onClick.bind(this));
      });

      const checker = this.querySelector('[name="checker"]');
      if (checker) {
        checker.addEventListener("change", this.onChange.bind(this));
      }
    }

    addAccessibilityAttributes() {
      this.querySelectorAll(".list-card-product__listbox a").forEach((anchor) => {
        anchor.setAttribute("role", "button");
      });
    }

    isSupplement() {
      return this.hasAttribute('data-is-supplement');
    }

    isFlavorOnlySupplement() {
      if (!this.isSupplement()) return false;
      const actionsContainer = this.querySelector('[data-card-actions]');
      return actionsContainer ? actionsContainer.hasAttribute('data-flavor-only') : false;
    }

    getSupplementDrawerData() {
      const handle = this.getAttribute('handle');
      const setHandle = this.getAttribute('data-set-handle') || '';
      const titleEl = this.querySelector('[data-card-title]');
      const imageEl = this.querySelector('[data-card-primary-image]');
      const infoLink = this.querySelector('[data-card-info-link]');
      const priceEl = this.querySelector('[data-card-price] .price-item--regular');

      let price = 0;
      if (priceEl) {
        const raw = priceEl.textContent.replace(/[^0-9.]/g, '');
        price = Math.round(parseFloat(raw) * 100) || 0;
      }

      return {
        handle,
        title: titleEl?.textContent?.trim() || '',
        imageUrl: imageEl?.src || imageEl?.getAttribute('data-src') || '',
        price,
        productUrl: infoLink?.href || `/products/${handle}`,
        setHandle,
      };
    }

    openSupplementDrawer() {
      const drawer = document.querySelector('product-quick-add-drawer');
      if (drawer) {
        drawer.open(this.getSupplementDrawerData());
      }
    }

    initializeVariantHandling() {
      const productCard = this.querySelector('.list-card-product');
      if (!productCard) return;

      // Supplements always open the quick-add drawer, EXCEPT flavor-only ones (no size, no set- tag)
      if (this.isSupplement() && !this.isFlavorOnlySupplement()) {
        this.cleanupVariantListeners();
        this.initializeSupplementClickHandler();
        return;
      }

      if (!productCard.classList.contains('hasVariants')) return;

      const productCTA = this.querySelector('.cartContainer');
      const productMedia = this.querySelector('.mediaWrapper');
      const cartModal = this.querySelector('.cartModal');
      const cardRoot = this.querySelector('.productCardRoot');

      if (!cartModal) return;

      // Clean up existing listeners
      this.cleanupVariantListeners();

      if (this.mql.matches) {
        // Desktop behavior - hover on quick-add icon to show inline cartModal
        const backupVariants = productCTA?.querySelector('.backup-hasVariants');

        if (backupVariants) {
          const mouseEnterHandler = () => {
            this.eventMouseEnterHappened = true;
            this.resetOverloadSteps();
            cartModal.classList.add('open');
            cardRoot?.classList.add('is-open');
          };
          const mouseLeaveHandler = () => {
            this.eventMouseEnterHappened = false;
            cartModal.classList.remove('open');
            cardRoot?.classList.remove('is-open');
          };

          // Hover on icon
          backupVariants.addEventListener('mouseenter', mouseEnterHandler);
          backupVariants.addEventListener('mouseleave', mouseLeaveHandler);
          backupVariants._mouseEnterHandler = mouseEnterHandler;
          backupVariants._mouseLeaveHandler = mouseLeaveHandler;

          // Keep modal open while hovering over it
          cartModal.addEventListener('mouseenter', mouseEnterHandler);
          cartModal.addEventListener('mouseleave', mouseLeaveHandler);
          cartModal._mouseEnterHandler = mouseEnterHandler;
          cartModal._mouseLeaveHandler = mouseLeaveHandler;
        }
      } else {
        // Mobile behavior - click opens pdp-quickadd-modal
        [productCTA].forEach(element => {
          if (element) {
            const backupVariants = element.querySelector('.backup-hasVariants');
            const closeButton = element.querySelector('button.dialogCloseButton');

            if (backupVariants) {
              const clickHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                document.querySelector('pdp-quickadd-modal').renderContent(this);
                document.body.classList.add('overflow-hidden');
              };

              backupVariants.addEventListener('click', clickHandler);
              backupVariants._clickHandler = clickHandler;
            }

            if (closeButton) {
              const closeHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                cartModal.classList.remove('open');
                document.body.classList.remove('overflow-hidden');
              };

              closeButton.addEventListener('click', closeHandler);
              closeButton._closeHandler = closeHandler;
            }
          }
        });
      }
    }

    initializeSupplementClickHandler() {
      const productCTA = this.querySelector('.cartContainer');
      if (!productCTA) return;

      const btn = productCTA.querySelector('.card-product__select-flavour, .backup-hasVariants, .backup-addToCart, .cartButton');
      if (!btn) return;

      if (btn._supplementClickHandler) {
        btn.removeEventListener('click', btn._supplementClickHandler);
      }

      btn._supplementClickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openSupplementDrawer();
      };
      btn.addEventListener('click', btn._supplementClickHandler);

      // Warm the quick-add drawer's product data on first hover / touch so the
      // drawer's Add button is ready the moment it opens (instead of waiting on
      // 2-3 product/collection fetches after the click).
      if (!btn._supplementPrefetchHandler) {
        btn._supplementPrefetchHandler = () => {
          const drawer = document.querySelector('product-quick-add-drawer');
          if (drawer && typeof drawer.prefetch === 'function') {
            drawer.prefetch(this.getAttribute('handle'), this.getAttribute('data-set-handle') || '');
          }
        };
        btn.addEventListener('mouseenter', btn._supplementPrefetchHandler, { once: true });
        btn.addEventListener('touchstart', btn._supplementPrefetchHandler, { once: true, passive: true });
      }
    }

    cleanupVariantListeners() {
      const productCTA = this.querySelector('.cartContainer');
      const productMedia = this.querySelector('.mediaWrapper');

      [productCTA].forEach(element => {
        if (element) {
          // Remove desktop listeners
          if (element._mouseEnterHandler) {
            element.removeEventListener('mouseenter', element._mouseEnterHandler);
            delete element._mouseEnterHandler;
          }
          if (element._mouseLeaveHandler) {
            element.removeEventListener('mouseleave', element._mouseLeaveHandler);
            delete element._mouseLeaveHandler;
          }

          // Remove mobile listeners
          const backupVariants = element.querySelector('.backup-hasVariants');
          const closeButton = element.querySelector('button.dialogCloseButton');

          if (backupVariants && backupVariants._clickHandler) {
            backupVariants.removeEventListener('click', backupVariants._clickHandler);
            delete backupVariants._clickHandler;
          }

          if (closeButton && closeButton._closeHandler) {
            closeButton.removeEventListener('click', closeButton._closeHandler);
            delete closeButton._closeHandler;
          }
        }
      });
    }

    initializeWishlistHandling() {
      const productCard = this.querySelector('.list-card-product');
      if (!productCard) return;

      const wishButton = this.querySelector('button.wishlistButton');
      if (!wishButton) return;

      // Get product data from button or card
      this.productData = this.getProductData();
      if (!this.productData) return;

      // Clean up existing listeners
      this.cleanupWishlistListeners();

      // Check if this is a single variant product (has backup-addToWishlist class on main button)
      const isSingleVariant = wishButton.classList.contains('backup-addToWishlist');

      if (this.mql.matches) {
        // Desktop behavior - hover
        const localModal = this.querySelector('.wishModal');
        const wishDialog = this.querySelector('.wishDialog');

        if (localModal && wishDialog) {
          // Use local modal for desktop hover (if it exists)
          [wishButton, wishDialog].forEach(element => {
            if (element) {
              const mouseEnterHandler = () => {
                this.eventMouseEnterHappened = true;
                localModal.classList.add('open');
                wishDialog?.classList.add('open');
              };

              const mouseLeaveHandler = () => {
                this.eventMouseEnterHappened = false;
                localModal.classList.remove('open');
                wishDialog?.classList.remove('open');
              };

              element.addEventListener('mouseenter', mouseEnterHandler);
              element.addEventListener('mouseleave', mouseLeaveHandler);

              element._wishMouseEnterHandler = mouseEnterHandler;
              element._wishMouseLeaveHandler = mouseLeaveHandler;
            }
          });
        }
      } else {
        // Mobile behavior - click
        if (wishButton && !isSingleVariant) {
          // For multi-variant products on mobile
          const clickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Check if we have a local modal (used in collection-tabs when wishlist_lightbox is false)
            const localModal = this.querySelector('.wishModal');
            const sharedModal = document.querySelector('.shared-wishlist-modal');

            console.log('sharedModal', sharedModal)

            if (sharedModal) {
              // Fall back to shared modal if no local modal
              this.openSharedWishlistModal();
            } else if (localModal) {
              // Use local modal if it exists
              this.openWishlistModal();
            }
          };

          wishButton.addEventListener('click', clickHandler);
          wishButton._wishClickHandler = clickHandler;

          // Add close button handler for local modal on mobile
          const localModal = this.querySelector('.wishModal');
          if (localModal) {
            const closeButton = localModal.querySelector('button.dialogCloseButton');
            if (closeButton) {
              const closeHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.closeWishlistModal();
              };

              closeButton.addEventListener('click', closeHandler);
              closeButton._wishModalCloseHandler = closeHandler;
            }
          }
        }
        // Note: Single variant products will be handled by the backup-addToWishlist class
        // through the existing handleWishlistClick method
      }
    }

    getProductData() {
      // Try to get product data from the wishlist button first
      const wishButton = this.querySelector('button.wishlistButton');
      if (wishButton && wishButton.dataset.productId) {
        return {
          productId: wishButton.dataset.productId,
          handle: wishButton.dataset.handle,
          title: wishButton.dataset.productTitle,
          image: wishButton.dataset.image,
          variantId: wishButton.dataset.variantId,
          price: wishButton.dataset.price,
          variantTitle: wishButton.dataset.variantTitle
        };
      }

      // Try to extract from rendered wishlistModal if it exists
      const localModal = this.querySelector('.wishModal');
      if (localModal) {
        const variantButtons = localModal.querySelectorAll('.backup-addToWishlist');
        if (variantButtons.length > 0) {
          const firstButton = variantButtons[0];
          return {
            productId: firstButton.dataset.productId,
            handle: firstButton.dataset.handle,
            title: firstButton.dataset.productTitle,
            image: firstButton.dataset.image,
            variants: Array.from(variantButtons).map(btn => ({
              id: btn.dataset.variantId,
              title: btn.dataset.variantTitle || btn.textContent.trim(),
              price: btn.dataset.price,
              productId: btn.dataset.productId,
              handle: btn.dataset.handle,
              productTitle: btn.dataset.productTitle,
              image: btn.dataset.image
            }))
          };
        }
      }

      return null;
    }

    cleanupWishlistListeners() {
      const wishButton = this.querySelector('button.wishlistButton');
      const wishDialog = this.querySelector('.wishDialog');
      const wishModalClose = this.querySelector('.wishModal button.dialogCloseButton');
      const backupWishButton = this.querySelector('button.backup-hasVariants-wishlist');

      [wishButton, wishDialog].forEach(element => {
        if (element) {
          if (element._wishMouseEnterHandler) {
            element.removeEventListener('mouseenter', element._wishMouseEnterHandler);
            delete element._wishMouseEnterHandler;
          }
          if (element._wishMouseLeaveHandler) {
            element.removeEventListener('mouseleave', element._wishMouseLeaveHandler);
            delete element._wishMouseLeaveHandler;
          }
        }
      });

      if (wishButton && wishButton._wishClickHandler) {
        wishButton.removeEventListener('click', wishButton._wishClickHandler);
        delete wishButton._wishClickHandler;
      }

      if (wishModalClose) {
        if (wishModalClose._wishCloseHandler) {
          wishModalClose.removeEventListener('click', wishModalClose._wishCloseHandler);
          delete wishModalClose._wishCloseHandler;
        }
        if (wishModalClose._wishModalCloseHandler) {
          wishModalClose.removeEventListener('click', wishModalClose._wishModalCloseHandler);
          delete wishModalClose._wishModalCloseHandler;
        }
      }

      if (backupWishButton && backupWishButton._backupWishClickHandler) {
        backupWishButton.removeEventListener('click', backupWishButton._backupWishClickHandler);
        delete backupWishButton._backupWishClickHandler;
      }
    }

    initializeWishlistButtonHandling() {
      const productCard = this.querySelector('.list-card-product');
      if (!productCard) return;

      const productCTA = this.querySelector('.cartContainer');
      const wishlistButton = productCTA?.querySelector('.backupWishlistButton.backup-hasVariants');
      const cartModal = this.querySelector('.cartModal');

      if (!wishlistButton || !cartModal) return;

      // Clean up existing listeners
      this.cleanupWishlistButtonListeners();

      if (this.mql.matches) {
        // Desktop behavior - click (same as mobile)
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          cartModal.classList.add('open');
          document.body.classList.add('overflow-hidden');
        };

        wishlistButton.addEventListener('click', clickHandler);
        wishlistButton._wishlistClickHandler = clickHandler;

        const closeButton = cartModal.querySelector('button.dialogCloseButton');
        if (closeButton) {
          const closeHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            cartModal.classList.remove('open');
            document.body.classList.remove('overflow-hidden');
          };

          closeButton.addEventListener('click', closeHandler);
          closeButton._wishlistCloseHandler = closeHandler;
        }
      } else {
        // Mobile behavior - click
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          cartModal.classList.add('open');
          document.body.classList.add('overflow-hidden');
        };

        wishlistButton.addEventListener('click', clickHandler);
        wishlistButton._wishlistClickHandler = clickHandler;

        const closeButton = cartModal.querySelector('button.dialogCloseButton');
        if (closeButton) {
          const closeHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            cartModal.classList.remove('open');
            document.body.classList.remove('overflow-hidden');
          };

          closeButton.addEventListener('click', closeHandler);
          closeButton._wishlistCloseHandler = closeHandler;
        }
      }
    }

    cleanupWishlistButtonListeners() {
      const productCTA = this.querySelector('.cartContainer');
      const wishlistButton = productCTA?.querySelector('.backupWishlistButton.backup-hasVariants');
      const cartModal = this.querySelector('.cartModal');
      const closeButton = cartModal?.querySelector('button.dialogCloseButton');

      if (productCTA) {
        if (productCTA._wishlistMouseEnterHandler) {
          productCTA.removeEventListener('mouseenter', productCTA._wishlistMouseEnterHandler);
          delete productCTA._wishlistMouseEnterHandler;
        }
        if (productCTA._wishlistMouseLeaveHandler) {
          productCTA.removeEventListener('mouseleave', productCTA._wishlistMouseLeaveHandler);
          delete productCTA._wishlistMouseLeaveHandler;
        }
      }

      if (wishlistButton && wishlistButton._wishlistClickHandler) {
        wishlistButton.removeEventListener('click', wishlistButton._wishlistClickHandler);
        delete wishlistButton._wishlistClickHandler;
      }

      if (closeButton && closeButton._wishlistCloseHandler) {
        closeButton.removeEventListener('click', closeButton._wishlistCloseHandler);
        delete closeButton._wishlistCloseHandler;
      }
    }

    initializeWishlistState() {
      // Initialize wishlist state checking when Swym is ready
      const checkWishlistState = () => {
        if (window._swat && window._swat.fetchWrtEventTypeET) {
          this.fetchAndUpdateWishlistState();
        }
      };

      // Check immediately if Swym is already loaded
      if (window._swat && window._swat.fetchWrtEventTypeET) {
        this.fetchAndUpdateWishlistState();
      } else {
        // Otherwise, add to Swym callbacks
        if (!window.SwymCallbacks) {
          window.SwymCallbacks = [];
        }
        window.SwymCallbacks.push(checkWishlistState);
      }
    }

    fetchAndUpdateWishlistState() {
      if (!window._swat || !window._swat.fetchWrtEventTypeET) return;

      window._swat.fetchWrtEventTypeET((products) => {
        this.updateWishlistButtonStates(products);
      }, 4); // et 4 stands for wishlist event type
    }

    updateWishlistButtonStates(wishlistProducts) {
      if (!wishlistProducts || !Array.isArray(wishlistProducts)) return;

      // Get all wishlist buttons in this product card
      const allWishlistButtons = this.querySelectorAll('.backup-addToWishlist');
      let anyVariantInWishlist = false;

      allWishlistButtons.forEach(button => {
        const variantId = button.dataset.variantId;
        const productId = button.dataset.productId;

        if (!variantId || !productId) return;

        // Check if this variant is in the wishlist
        const isInWishlist = wishlistProducts.find(p => {
          return p.empi == productId && p.epi == variantId;
        });

        if (isInWishlist) {
          this.markButtonAsSaved(button);
          anyVariantInWishlist = true;
        } else {
          this.markButtonAsUnsaved(button);
        }
      });

      // Update the main wishlist button SVG if any variant is in wishlist
      const mainWishlistButton = this.querySelector('button.wishlistButton');
      if (mainWishlistButton) {
        const svg = mainWishlistButton.querySelector('svg.wishlist-heart');
        if (svg) {
          if (anyVariantInWishlist) {
            svg.classList.add('is-filled');
            mainWishlistButton.classList.add('is-saved');
          } else {
            svg.classList.remove('is-filled');
            mainWishlistButton.classList.remove('is-saved');
          }
        }
      }
    }

    markButtonAsSaved(button) {
      // Update button appearance to show it's saved
      button.classList.add('is-saved');
      // Don't set disabled — it triggers browser-native strikethrough/grey styling.
      // pointer-events: none is applied via CSS on .is-saved instead.
      button.setAttribute('aria-label', 'Saved to wishlist');

      // Update SVG to show filled state via CSS
      const svg = button.querySelector('svg.wishlist-heart');
      if (svg) {
        svg.classList.add('is-filled');
      }
    }

    markButtonAsUnsaved(button) {
      // Reset button to unsaved state
      button.classList.remove('is-saved');
      button.removeAttribute('disabled');
      button.setAttribute('aria-label', window.accessibilityStrings?.addToWishlist || 'Add to wishlist');

      // Update SVG to show outline state via CSS
      const svg = button.querySelector('svg.wishlist-heart');
      if (svg) {
        svg.classList.remove('is-filled');
      }
    }

    openSharedWishlistModal() {
      const sharedModal = document.querySelector('.shared-wishlist-modal');
      if (!sharedModal || !this.productData) return;

      // Populate modal with product data
      this.populateSharedModal(sharedModal, this.productData);

      // Show modal
      sharedModal.classList.add('open');
      sharedModal.querySelector('.wishDialog')?.classList.add('open');
      document.body.classList.add('overflow-hidden');

      // Setup close button listener (one-time)
      const closeButton = sharedModal.querySelector('button.dialogCloseButton');
      if (closeButton && !closeButton._sharedCloseHandler) {
        closeButton._sharedCloseHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeSharedWishlistModal();
        };
        closeButton.addEventListener('click', closeButton._sharedCloseHandler);
      }
    }

    closeSharedWishlistModal() {
      const sharedModal = document.querySelector('.shared-wishlist-modal');
      if (sharedModal) {
        sharedModal.classList.remove('open');
        sharedModal.querySelector('.wishDialog')?.classList.remove('open');
        document.body.classList.remove('overflow-hidden');
      }
    }

    populateSharedModal(modal, productData) {
      // Update image
      const modalImage = modal.querySelector('.shared-modal-image');
      if (modalImage && productData.image) {
        // productData.image is already the 512px CDN URL (2×); derive a 1× version
        const img2x = productData.image;
        const img1x = img2x.replace(/width=\d+/, 'width=256');
        modalImage.src = img1x;
        modalImage.srcset = `${img1x} 1x, ${img2x} 2x`;
        modalImage.alt = productData.title || '';
      }

      // Update title
      const titleParts = productData.title ? productData.title.split(' - ') : [];
      const titleElement = modal.querySelector('.dialogItemTitle');
      const subtitleElement = modal.querySelector('.dialogItemSubtitle');

      if (titleElement) {
        titleElement.textContent = titleParts[0] || '';
      }
      if (subtitleElement) {
        subtitleElement.textContent = titleParts[1] || '';
      }

      // Update price (if we can access it)
      const priceContainer = modal.querySelector('.shared-modal-price');
      if (priceContainer && productData.price) {
        priceContainer.innerHTML = `<span class="price">${this.formatPrice(productData.price)}</span>`;
      }

      // Populate variants
      const variantsContainer = modal.querySelector('.shared-modal-variants');
      if (variantsContainer && productData.variants) {
        variantsContainer.innerHTML = '';
        productData.variants.forEach(variant => {
          const button = document.createElement('button');
          button.className = 'dialogListItem backup-addToWishlist';
          button.setAttribute('data-variant-id', variant.id);
          button.setAttribute('data-product-id', variant.productId);
          button.setAttribute('data-handle', variant.handle);
          button.setAttribute('data-product-title', variant.productTitle);
          button.setAttribute('data-image', variant.image);
          button.setAttribute('data-price', variant.price);
          button.setAttribute('data-variant-title', variant.title);
          button.textContent = variant.title === 'Default Title' ? 'Add to wishlist' : variant.title;

          // Add click handler
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.addToWishlist(button.dataset, button);
          });

          variantsContainer.appendChild(button);
        });
      }
    }

    formatPrice(cents) {
      // Simple price formatter - adjust based on your currency format
      const dollars = (parseInt(cents) / 100).toFixed(2);
      return `$${dollars}`;
    }

    openWishlistModal() {
      const wishModal = this.querySelector('.wishModal');
      if (wishModal) {
        wishModal.classList.add('open');
        wishModal.querySelector('.wishDialog')?.classList.add('open');
        document.body.classList.add('overflow-hidden');
      }
    }

    closeWishlistModal() {
      const wishModal = this.querySelector('.wishModal');
      if (wishModal) {
        wishModal.classList.remove('open');
        wishModal.querySelector('.wishDialog')?.classList.remove('open');
        document.body.classList.remove('overflow-hidden');
      }
    }

    handleAddToCart(event, variant = false) {
      const button = event.target.closest('button.backup-addToCart');
      if (button) {
        // Supplements go through the quick-add drawer, EXCEPT flavor-only ones
        if (this.isSupplement() && !this.isFlavorOnlySupplement()) {
          event.preventDefault();
          event.stopPropagation();
          this.openSupplementDrawer();
          return;
        }
        // Prevent spam clicks while loading
        if (button.classList.contains('loading')) return;
        event.preventDefault();
        event.stopPropagation();
        const variantId = variant || button.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId, button);
        }
      }
    }

    handleOverloadOptionClick(event) {
      const opt1Btn = event.target.closest('.overload-opt1-btn');
      if (opt1Btn) {
        event.preventDefault();
        event.stopPropagation();
        const cartActions = opt1Btn.closest('.cartActions');
        // Highlight selected size button, keep step 1 visible
        cartActions.querySelectorAll('.overload-opt1-btn').forEach(b => b.classList.remove('is-selected'));
        opt1Btn.classList.add('is-selected');
        // Hide all step 2 panels, then show the matching one
        cartActions.querySelectorAll('[data-step="2"]').forEach(s => s.classList.add('overload-step--hidden'));
        const opt1Value = opt1Btn.dataset.option1;
        const step2 = cartActions.querySelector(`[data-step="2"][data-for-option1="${CSS.escape(opt1Value)}"]`);
        if (step2) step2.classList.remove('overload-step--hidden');
        return;
      }
    }

    resetOverloadSteps() {
      const cartActions = this.querySelector('.cartActions');
      if (!cartActions) return;
      const step1 = cartActions.querySelector('[data-step="1"]');
      const step2s = cartActions.querySelectorAll('[data-step="2"]');
      if (step1) step1.classList.remove('overload-step--hidden');
      step2s.forEach(s => s.classList.add('overload-step--hidden'));
      cartActions.querySelectorAll('.overload-opt1-btn').forEach(b => b.classList.remove('is-selected'));
    }

    handleWishlistClick(event) {
      const button = event.target.closest('button.backup-addToWishlist');
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        this.addToWishlist(button.dataset, button);
      }
    }

    async addToCart(variantId, element) {
      if (!variantId || !element) return;

      try {
        element.classList.add('loading');
        element.setAttribute('aria-busy', 'true');

        // Use fetchConfig utility for consistent configuration
        const config = fetchConfig();
        config.body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
          properties: {}
        });

        // Resilient parse: when the cart API is throttled by rapid adds Shopify
        // returns an HTML challenge page (HTTP 429/430), not JSON. Routing through
        // fetchCartJSON turns that into a friendly message + one auto-retry instead
        // of the raw `Unexpected token <, "<!DOCTYPE"...` error a shopper used to see.
        // serializeCartAdd queues rapid adds so they don't fire concurrently.
        const doAdd = () => window.fetchCartJSON(window.routes.cart_add_url, config);
        const { response, json } = await (window.serializeCartAdd ? window.serializeCartAdd(doAdd) : doAdd());

        if (!response.ok || json.status) {
          // Handle errors similar to product-form.js
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'list-card-product',
            productVariantId: variantId,
            errors: json.errors || json.description,
            message: json.message || 'Failed to add item to cart',
          });

          throw new Error(json.description || json.message || `HTTP error! status: ${response.status}`);
        }

        // Render the cart icon + success toast immediately from the add response.
        // (The cart drawer is intentionally excluded from the add request — see
        // getSectionsToRender — so this stays fast; the drawer refreshes on open.)
        this.renderSections(json);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'list-card-product',
          cartData: json,
          productVariantId: variantId
        });

        // Close modal
        const cartModal = element.closest('.cartModal');
        if (cartModal) {
          cartModal.classList.remove('open');
        }

        if (document.body.classList.contains('overflow-hidden')) {
          document.body.classList.remove('overflow-hidden');
        }

        // Mark the cart drawer stale so it re-renders on next open — or refreshes
        // now if it's already open (markCartDrawerStale in cart-drawer.js).
        if (typeof window.markCartDrawerStale === 'function') window.markCartDrawerStale();
        else window.cartDrawerStale = true;

        this.showSuccess('Item added to cart successfully!');

        // Secondary cart chores (gift/donation + count/progress/empty-state
        // refresh) are debounced so rapid multi-add from product cards does not
        // stampede the cart API. See global.js runPostAddChores().
        if (typeof window.runPostAddChores === 'function') {
          window.runPostAddChores();
        } else if (typeof window.handleCartUpdated === 'function') {
          window.handleCartUpdated(json);
        } else {
          window.cartJSON = json;
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError(error.message || 'Failed to add item to cart. Please try again.');
      } finally {
        element.classList.remove('loading');
        element.setAttribute('aria-busy', 'false');
      }
    }

    getSectionsToRender() {
      // NOTE: 'cart-drawer' is deliberately excluded — it is the heaviest section to
      // server-render and the drawer does not open from quick-add. It is refreshed
      // lazily the next time the cart drawer is opened (see cart-drawer.js open()).
      return [
        {
          id: "cart-notification-products",
          section: "cart-notification-products",
          selector: `[id="cart-notification-product-${this.productId}"]`,
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: "cart-notifications-wrapper",
          section: "cart-notifications",
        },
        // 'mini-cart-upsell' removed — its toast container is commented out, so it
        // was server-rendered + discarded on every add. Re-add to re-enable.
      ];
    }

    renderSections(parsedState) {
      // The <cart-notification> component renders ALL of these sections (products,
      // upsell, cart-icon, toast wrapper) with correct per-item targeting derived
      // from the add response. Prefer it and skip the redundant second render that
      // used to run here — it targeted `cart-notification-product-${this.productId}`
      // but this.productId was never defined, so it wiped + re-parsed each section.
      if (this.cartNotification) {
        this.cartNotification.renderContents(parsedState);
        return;
      }

      // Fallback only when there is no cart-notification component on the page:
      // render the non-toast sections directly (cart icon + toast wrapper).
      this.getSectionsToRender().forEach((section) => {
        if (section.section === 'cart-notification-products' || section.section === 'mini-cart-upsell') return;
        this.renderOneSection(section, parsedState);
      });
    }

    renderOneSection(section, parsedState) {
      const sectionElement = document.getElementById(section.id);
      if (!sectionElement) return;

      if (section.section === 'cart-drawer') {
        sectionElement.closest('cart-drawer')?.classList.remove('is-empty');
      }

      const elementToReplace =
        sectionElement && sectionElement.querySelector(section.selector)
          ? sectionElement.querySelector(section.selector)
          : sectionElement;

      if (elementToReplace && parsedState.sections && parsedState.sections[section.section]) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    }

    async addToWishlist(dataset, element) {
      if (!dataset || !element) return;

      const data = {
        epi: parseInt(dataset.variantId),
        empi: parseInt(dataset.productId),
        du: `${window.location.origin}/products/${dataset.handle}`,
        dt: dataset.productTitle,
        iu: dataset.image,
        pr: Number(dataset.price),
        vi: dataset.variantTitle,
      };

      try {
        // const wishlistButton = element.closest('.wishContainer')?.querySelector('.wishlistButton');
        const wishlistButton = element.closest('.card-product')?.querySelector('.wishlistButton');
        if (wishlistButton) {
          wishlistButton.classList.add('loading');
          wishlistButton.setAttribute('aria-busy', 'true');
        }

        if (window._swat && window._swat.addToWishList) {
          await new Promise((resolve, reject) => {
            window._swat.addToWishList(data, (success) => {
              if (success) {
                resolve();
              } else {
                reject(new Error('Wishlist API returned false'));
              }
            });
          });
        } else {
          throw new Error('Wishlist functionality not available');
        }

        // Refresh wishlist state to update all button states
        this.fetchAndUpdateWishlistState();

        this.showSuccess('Item added to wishlist successfully!');

      } catch (error) {
        console.error('Error adding to wishlist:', error);
        this.showError('Failed to add item to wishlist. Please try again.');
      } finally {
        const loadingElements = document.querySelectorAll('.wishContainer .loading');
        loadingElements.forEach(el => {
          el.classList.remove('loading');
          el.setAttribute('aria-busy', 'false');
        });

        // Close the local wishlist modal if open
        this.closeWishlistModal();

        // Close the shared wishlist modal if open
        this.closeSharedWishlistModal();

        // Also close the cart modal if it's being used for wishlist (coming soon products)
        const cartModal = this.querySelector('.cartModal');
        if (cartModal && cartModal.classList.contains('open')) {
          cartModal.classList.remove('open');
          document.body.classList.remove('overflow-hidden');
        }
      }
    }

    triggerAutoFunctions() {
      // Trigger auto gift functionality
      if (typeof window.runAutoGift === 'function') {
        window.runAutoGift();
      }

      // Trigger auto donation functionality
      if (typeof window.runAutoDonation === 'function') {
        window.runAutoDonation();
      }
    }

    showSuccess(message) {
      this.showNotification(message, 'success');
    }

    showError(message) {
      this.showNotification(message, 'error');
    }

    showNotification(message, type = 'error') {
      const className = `list-card-${type}-notification`;
      let notification = document.querySelector(`.${className}`);

      if (!notification) {
        notification = document.createElement('div');
        notification.className = className;
        notification.setAttribute('role', 'alert');
        notification.setAttribute('aria-live', 'polite');
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${type === 'success' ? '#28a745' : '#dc3545'};
          color: white;
          padding: 12px 16px;
          border-radius: 4px;
          z-index: 9999;
          font-size: 14px;
          max-width: 300px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: translateX(100%);
          transition: transform 0.3s ease;
        `;
        document.body.appendChild(notification);
      }

      notification.textContent = message;
      notification.style.transform = 'translateX(0)';

      // Clear any existing timeout
      if (notification._timeout) {
        clearTimeout(notification._timeout);
      }

      notification._timeout = setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (notification && notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 300);
      }, 5000);
    }

    // ==========================================
    // Swatch Methods
    // ==========================================

    initializeSwatches() {
      const swatchContainer = this.querySelector('[data-card-swatches]');
      if (!swatchContainer) return;

      // Merge this card's server-rendered product data into the shared
      // page-level registry (cards embed only their OWN product; siblings are
      // resolved through the registry or fetched on demand).
      collectSwatchProductData();

      // Attach click handlers to each swatch button
      const swatches = swatchContainer.querySelectorAll('.card-swatch');
      swatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.onSwatchClick(swatch);
        });
        // Warm any off-page sibling's data before the click so switching
        // stays instant.
        swatch.addEventListener('pointerenter', () => {
          const handle = swatch.dataset.productHandle;
          if (handle) ensureSwatchProductData(handle);
        }, { once: true });
      });

      // Run overflow check
      this.checkSwatchOverflow();

      // Overflow is now measured; reveal the real swatches and hide the skeleton
      swatchContainer.closest('.card-swatches-area')?.classList.add('is-ready');

      // Observe container for resize changes
      if (this._swatchResizeObserver) {
        this._swatchResizeObserver.disconnect();
      }
      this._swatchResizeObserver = new ResizeObserver(() => {
        this.checkSwatchOverflow();
      });
      this._swatchResizeObserver.observe(swatchContainer);
    }

    checkSwatchOverflow() {
      const container = this.querySelector('[data-card-swatches]');
      if (!container) return;

      const swatches = Array.from(container.querySelectorAll('.card-swatch'));
      const overflowEl = container.querySelector('[data-swatch-overflow]');
      if (!overflowEl || swatches.length === 0) return;

      // Reset: show all swatches, hide overflow indicator
      swatches.forEach(s => s.style.display = '');
      overflowEl.style.display = 'none';
      overflowEl.setAttribute('aria-hidden', 'true');

      const containerWidth = container.clientWidth;
      // Reserve space for the "+n" indicator (approx 30px)
      const overflowReserve = 30;
      let totalWidth = 0;
      let hiddenCount = 0;
      const gap = 6; // matches CSS gap

      for (let i = 0; i < swatches.length; i++) {
        const swatchWidth = swatches[i].offsetWidth + (i > 0 ? gap : 0);
        const isLast = i === swatches.length - 1;
        const limit = isLast ? containerWidth : containerWidth - overflowReserve;

        if (totalWidth + swatchWidth > limit) {
          // Hide this and all subsequent swatches
          for (let j = i; j < swatches.length; j++) {
            swatches[j].style.display = 'none';
            hiddenCount++;
          }
          break;
        }
        totalWidth += swatchWidth;
      }

      if (hiddenCount > 0) {
        overflowEl.textContent = `+${hiddenCount}`;
        overflowEl.style.display = '';
        overflowEl.setAttribute('aria-hidden', 'false');
        // Update overflow href to match current card link
        const cardInfoLink = this.querySelector('[data-card-info-link]');
        if (cardInfoLink) {
          overflowEl.href = cardInfoLink.href;
        }
      }
    }

    onSwatchClick(swatchEl) {
      const handle = swatchEl.dataset.productHandle;
      const productUrl = swatchEl.dataset.productUrl;
      if (!handle) return;

      // If already active, do nothing
      if (swatchEl.classList.contains('swatch--active')) return;

      // Update active state
      const container = swatchEl.closest('[data-card-swatches]');
      if (container) {
        container.querySelectorAll('.card-swatch').forEach(s => s.classList.remove('swatch--active'));
      }
      swatchEl.classList.add('swatch--active');

      // Registry hit is instant (server-rendered JSON); off-page siblings
      // resolve via a single fetch. Guard against the user clicking another
      // swatch while that fetch is in flight.
      ensureSwatchProductData(handle).then((productData) => {
        if (!productData) return;
        if (!swatchEl.classList.contains('swatch--active')) return;
        this.updateCardContent(productData, handle, productUrl);
      });
    }

    updateCardContent(productData, handle, productUrl) {
      if (!productData) return;

      const collectionHandle = this.querySelector('[data-card-swatches]')?.dataset.collectionHandle;
      const newUrl = productUrl || (collectionHandle
        ? `/collections/${collectionHandle}/products/${handle}`
        : `/products/${handle}`);

      // -- Update links --
      this.querySelectorAll('[data-card-info-link], [data-card-media-link]').forEach(link => {
        link.href = newUrl;
      });

      // Update overflow swatch link
      const overflowLink = this.querySelector('[data-swatch-overflow]');
      if (overflowLink) {
        overflowLink.href = newUrl;
      }

      // -- Update product handle and type on card root --
      const cardRoot = this.querySelector('.list-card-product');
      if (cardRoot) {
        cardRoot.dataset.productHandle = handle;
        if (productData.productType) {
          cardRoot.dataset.productType = productData.productType;
        }
        // Update hasVariants class
        const variants = productData.variants?.edges || [];
        if (variants.length > 1) {
          cardRoot.classList.add('hasVariants');
        } else {
          cardRoot.classList.remove('hasVariants');
        }
        // Update wishlistItem class based on coming_soon
        const tags = productData.tags || [];
        const comingSoon = tags.some(t => t === 'soon');
        if (comingSoon) {
          cardRoot.classList.add('wishlistItem');
        } else {
          cardRoot.classList.remove('wishlistItem');
        }
      }

      // -- Update productID and handle on list-card-product element --
      const numericProductId = productData.id.replace('gid://shopify/Product/', '');
      this.setAttribute('productID', numericProductId);
      this.setAttribute('handle', handle);

      // -- Update supplement attribute for quick-add drawer --
      // Accessories may carry the 'all products: supplements' tag for
      // merchandising, but should use the normal quick-add — not the
      // supplement flavour drawer — so never treat them as supplements.
      const tagsArr = productData.tags || [];
      const isAccessoryNow = (productData.productType || '').toLowerCase().includes('accessories')
        || tagsArr.some(t => (typeof t === 'string' ? t : '').toLowerCase() === 'accessories');
      const isSuppNow = !isAccessoryNow && tagsArr.some(t => (typeof t === 'string' ? t : '').toLowerCase().includes('all products: supplements'));
      if (isSuppNow) {
        this.setAttribute('data-is-supplement', 'true');
        const setTag = tagsArr.find(t => typeof t === 'string' && t.split('-')[0] === 'set');
        if (setTag) this.setAttribute('data-set-handle', setTag);
      } else {
        this.removeAttribute('data-is-supplement');
        this.removeAttribute('data-set-handle');
      }

      // -- Update images --
      this.updateCardImages(productData);

      // -- Update product badges --
      this.updateCardBadges(productData);

      // -- Update title & subtitle --
      this.updateCardTitleSubtitle(productData);

      // -- Update price --
      this.updateCardPrice(productData);

      // -- Update Quick Add / Wishlist buttons --
      this.updateCardActions(productData, handle, newUrl);

      // Trigger currency conversion if available
      if (window.convertCurrency) {
        window.convertCurrency(this);
      }
    }

    updateCardImages(productData) {
      const primaryImg = this.querySelector('[data-card-primary-image]');
      const secondaryImg = this.querySelector('[data-card-secondary-image]');

      if (primaryImg && productData.featuredImage) {
        const imgUrl = productData.featuredImage.url;
        const srcset = this.buildSrcset(imgUrl);
        primaryImg.src = this.buildImageUrl(imgUrl, 320);
        primaryImg.setAttribute('data-src', this.buildImageUrl(imgUrl, 320));
        primaryImg.setAttribute('data-srcset', srcset);
        primaryImg.setAttribute('srcset', srcset);
        primaryImg.alt = productData.featuredImage.altText || productData.title;
        primaryImg.width = productData.featuredImage.width || '';
        primaryImg.height = productData.featuredImage.height || '';
      }

      if (secondaryImg && productData.images?.edges?.length > 1) {
        const secondImg = productData.images.edges[1].node;
        const imgUrl = secondImg.url;
        const srcset = this.buildSrcset(imgUrl);
        secondaryImg.src = this.buildImageUrl(imgUrl, 320);
        secondaryImg.setAttribute('data-src', this.buildImageUrl(imgUrl, 320));
        secondaryImg.setAttribute('data-srcset', srcset);
        secondaryImg.setAttribute('srcset', srcset);
        secondaryImg.alt = secondImg.altText || productData.title;
        secondaryImg.width = secondImg.width || '';
        secondaryImg.height = secondImg.height || '';
        secondaryImg.style.display = '';
        if (primaryImg) {
          primaryImg.classList.add('has-hover-image');
        }
      } else if (secondaryImg) {
        secondaryImg.style.display = 'none';
        if (primaryImg) {
          primaryImg.classList.remove('has-hover-image');
        }
      }
    }

    buildSrcset(baseUrl) {
      return [320, 384, 640, 768, 1024, 1296, 1512].map(w => `${this.buildImageUrl(baseUrl, w)} ${w}w`).join(', ');
    }

    buildImageUrl(baseUrl, width) {
      // Shopify CDN URL transformation
      // For Storefront API urls like //cdn.shopify.com/.../files/image.jpg
      // We need to append ?width=X or use the _WIDTHx format
      if (!baseUrl) return '';
      try {
        const url = new URL(baseUrl.startsWith('//') ? `https:${baseUrl}` : baseUrl);
        url.searchParams.set('width', width);
        return url.toString();
      } catch {
        return baseUrl;
      }
    }

    updateCardBadges(productData) {
      const badgeWrapper = this.querySelector('[data-card-badge-wrapper]');
      if (!badgeWrapper) return;

      const tags = productData.tags || [];
      const tagsLower = tags.map(t => (typeof t === 'string' ? t : '').toLowerCase());
      const isAvailable = productData.availableForSale;
      const productType = (productData.productType || '').toLowerCase();
      const isSupplement = tagsLower.some(t => t.includes('all products: supplements'));
      const comingSoon = tags.some(t => t === 'soon');
      const preorder = tags.some(t => t === 'preorder');
      const womensSizing = tags.some(t => t === 'womens-photo-preferred');
      const oversizedFit = tagsLower.some(t => t.includes('badge:oversized fit') || t.includes('badge: oversized fit'));
      const veganFriendly = tagsLower.some(t => t.includes('badge:vegan friendly'));
      const newFormula = tagsLower.some(t => t.includes('badge:new formula'));
      const lowCarb = tags.some(t => t === 'Low Carb');

      // Update wrapper classes
      if (isAvailable) {
        badgeWrapper.classList.remove('productSoldOut');
      } else {
        badgeWrapper.classList.add('productSoldOut');
      }
      const hasSetTagBadge = tags.some(t => typeof t === 'string' && t.toLowerCase().startsWith('set-'));
      const hasSizeOptionBadge = (productData.options || []).some(opt => opt.name.toLowerCase() === 'size');
      const isFlavorOnlySuppBadge = isSupplement && !hasSetTagBadge && !hasSizeOptionBadge;
      if (isSupplement && !isFlavorOnlySuppBadge) {
        badgeWrapper.classList.add('isSupplement');
      } else {
        badgeWrapper.classList.remove('isSupplement');
      }

      // Rebuild product badges (matches badges.liquid logic)
      const badgesContainer = this.querySelector('[data-card-product-badges]');
      if (badgesContainer) {
        // Extract custom badges from tags (badge:text format)
        const customBadges = [];
        for (const tag of tags) {
          if (typeof tag === 'string' && tag.includes('badge:')) {
            const parts = tag.split(':');
            if (parts.length > 1) {
              customBadges.push(parts[1]);
            }
          }
        }

        // Check for compare_at_price (sale badge)
        const variants = productData.variants?.edges || [];
        const firstVariant = variants[0]?.node;
        const hasCompareAtPrice = firstVariant?.compareAtPrice && parseFloat(firstVariant.compareAtPrice.amount) > parseFloat(firstVariant.price?.amount || 0);

        let badgeHtml = '';

        // Visual percentage badge (settings-driven, keep existing if present)
        // We can't access theme settings from JS, so we preserve the global badge and visual percentage badge
        const globalBadge = badgesContainer.querySelector('.top-left-badge')?.closest('.product_badges.top');
        // Note: global badge and visual percentage/promo badges are settings-driven and don't change per-product

        // Product-specific badges (from badges.liquid)
        if (comingSoon) {
          badgeHtml += '<span class="badge __Coming_Soon">Coming Soon</span>';
        } else if (isAvailable) {
          for (const badge of customBadges) {
            const badgeLower = badge.toLowerCase();
            // Skip badges that are rendered separately
            if (badgeLower === 'vegan friendly' || badgeLower === 'new formula' || badgeLower === 'oversized fit') continue;
            const sanitized = this.escapeHtml(badge);
            const className = badge.replace(/ /g, '_').replace(/:/g, '_').replace(/%/g, '_');
            badgeHtml += `<span class="badge __${className}${preorder && isAvailable ? ' badgePreorder' : ''}">${sanitized}</span>`;
          }
        }

        // Women's sizing badge
        if (womensSizing) {
          badgeHtml += '<span class="badge">Men\'s Sizing</span>';
        }

        // Oversized fit badge
        if (oversizedFit) {
          badgeHtml += '<span class="badge">Oversized Fit</span>';
        }

        // Supplement badges
        if (isSupplement && isAvailable) {
          if (veganFriendly) {
            badgeHtml += '<span class="badge badge_bottom isSupplement">Vegan Friendly</span>';
          } else if (newFormula) {
            badgeHtml += '<span class="badge badge_bottom isSupplement">New Formula</span>';
          }
        }

        // Low carb supplement
        if (lowCarb && isAvailable) {
          badgeHtml += '<span class="badge badge_bottom isSupplement">Low Carb</span>';
        }

        badgesContainer.innerHTML = badgeHtml;
      }

      // Hide sold-out overlay — the OOS banner in cartContainer handles the visual
      const soldOutEl = this.querySelector('[data-card-sold-out]');
      if (soldOutEl) {
        soldOutEl.style.display = 'none';
        soldOutEl.textContent = '';
      }
    }

    updateCardTitleSubtitle(productData) {
      const titleParts = productData.title.split(' - ');
      const subtitle = titleParts.slice(1).join(' - ');
      const tags = productData.tags || [];

      // Update subtitle block
      const subtitleEl = this.querySelector('[data-card-subtitle]');
      if (subtitleEl) {
        let html = `<p class="subtitle">${this.escapeHtml(subtitle)}</p>`;

        // Parse tags for feature lines
        const tagsLower = tags.map(t => (typeof t === 'string' ? t : '').toLowerCase());
        const productType = (this.querySelector('.list-card-product')?.dataset.productType || '').toLowerCase();
        const isBra = productType.includes('sports bras') || productType.includes('bralettes');
        const isTee = productType.includes('t-shirts');

        // Support type (bras)
        if (isBra) {
          for (const t of tagsLower) {
            if (t === 'facet:support:everyday support') { html += '<p class="subtitle"><i>Everyday Support</i></p>'; break; }
            if (t === 'facet:support:medium support') { html += '<p class="subtitle"><i>Medium Support</i></p>'; break; }
            if (t === 'facet:support:maximum support') { html += '<p class="subtitle"><i>Maximum Support</i></p>'; break; }
          }
        }

        // Seamless fabric
        if (tagsLower.some(t => t.includes('seamless-fabric'))) {
          html += '<p class="subtitle"><i>Seamless Fabric</i></p>';
        }

        // Fabric weight (tees)
        if (isTee) {
          for (const t of tagsLower) {
            if (t === 'facet:weight:heavyweight') { html += '<p class="subtitle"><i>Heavy-Weight Fabric</i></p>'; break; }
            if (t === 'facet:weight:mid-weight') { html += '<p class="subtitle"><i>Mid-Weight Fabric</i></p>'; break; }
            if (t === 'facet:weight:lightweight') { html += '<p class="subtitle"><i>Lightweight Fabric</i></p>'; break; }
            if (t === 'facet:weight:lightweight-rib-fabric') { html += '<p class="subtitle"><i>Lightweight Rib Fabric</i></p>'; break; }
          }
        }

        // Product cut
        for (const t of tagsLower) {
          if (t === 'facet:cut:cropped-slouchy-fit') { html += '<p class="subtitle"><i>Cropped Slouchy Fit</i></p>'; break; }
          if (t === 'facet:cut:shorter-boxy-fit') { html += '<p class="subtitle"><i>Shorter Boxy Fit</i></p>'; break; }
          if (t === 'facet:cut:boxy-fit') { html += '<p class="subtitle"><i>Boxy Fit</i></p>'; break; }
          if (t === 'facet:cut:mid-rise') { html += '<p class="subtitle"><i>Mid Rise</i></p>'; break; }
        }

        subtitleEl.innerHTML = html;
      }
    }

    formatMoney(amount) {
      const num = parseFloat(amount) || 0;
      return '$' + num.toFixed(2);
    }

    updateCardPrice(productData) {
      const priceContainer = this.querySelector('[data-card-price]');
      if (!priceContainer) return;

      const variants = productData.variants?.edges || [];
      const firstVariant = variants[0]?.node;
      if (!firstVariant) return;

      const price = parseFloat(firstVariant.price?.amount || 0);
      const compareAtPrice = firstVariant.compareAtPrice ? parseFloat(firstVariant.compareAtPrice.amount) : 0;

      // Mirror the server markup from snippets/product-price.liquid exactly so a swatch
      // swap yields the same DOM + classes as the initial render — no layout shift, and the
      // badge keeps its rounded pill and --sale-badge-* colours (instead of the old flat
      // 'sale_badge_text' structure, which reflowed the row and flipped the badge colours).
      const onSale = compareAtPrice > price && compareAtPrice > 0;
      let priceHtml = '';
      if (onSale) {
        const savePercent = 100 - Math.round((price * 100) / compareAtPrice);
        priceHtml = `
          <div class="price price--on-sale price--show-badge">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular price-item--last"><span class="money">${this.formatMoney(price)}</span></span>
            </div>
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price__compare"><s class="price-item price-item--regular"><span class="money">${this.formatMoney(compareAtPrice)}</span></s></span>
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last"><span class="money">${this.formatMoney(price)}</span></span>
            </div>
            <div class="price_badges_wrapper">
              <span class="badge price__badge-sale" aria-hidden="true">SAVE ${savePercent}%</span>
              <span class="badge price__badge-sold-out" aria-hidden="true">Sold out</span>
            </div>
          </div>
        `;
      } else {
        priceHtml = `
          <div class="price price--show-badge">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular price-item--last"><span class="money">${this.formatMoney(price)}</span></span>
            </div>
          </div>
        `;
      }

      priceContainer.innerHTML = priceHtml;
    }

    updateCardActions(productData, handle, newUrl) {
      const actionsContainer = this.querySelector('[data-card-actions]');
      if (!actionsContainer) return;

      const variants = productData.variants?.edges || [];
      const isAvailable = productData.availableForSale;
      const isSingleVariant = variants.length === 1;
      const isMultiVariant = variants.length > 1;
      const firstVariant = variants[0]?.node;
      const tags = productData.tags || [];
      const comingSoon = tags.some(t => t === 'soon');
      // no-quick-add: the card never adds to cart (card-product.liquid and src/helpers/isSupplement.ts read the same tag)
      const noQuickAdd = tags.some(t => typeof t === 'string' && t.toLowerCase().trim() === 'no-quick-add');
      const featuredImageUrl = productData.featuredImage?.url || '';
      const titleParts = productData.title.split(' - ');
      const numericProductId = productData.id.replace('gid://shopify/Product/', '');

      // Detect shop_at tag
      let shopAt = '';
      for (const t of tags) {
        if (typeof t === 'string') {
          const parts = t.split(':');
          if (parts[0] === 'shop_at' && parts[1]) {
            shopAt = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
            break;
          }
        }
      }

      // Detect set_types flow (metafield-driven, each type = separate product)
      const setTypes = Array.isArray(productData.set_types) && productData.set_types.length > 0
        ? productData.set_types
        : null;
      const hasSetTypes = !!setTypes;

      // Detect two-step layout: primary option (flavour/colour) + size option (same product)
      const options = productData.options || [];
      let sizeOptIdx = null, primaryOptIdx = null, primaryOptName = null, sizeOptName = null;
      if (!hasSetTypes) {
        options.forEach((opt, i) => {
          const nameLower = opt.name.toLowerCase();
          if (nameLower === 'size') {
            sizeOptIdx = i;
            sizeOptName = opt.name;
          } else if (primaryOptIdx === null) {
            primaryOptIdx = i;
            primaryOptName = opt.name;
          }
        });
      }
      const hasTwoStep = primaryOptIdx !== null && sizeOptIdx !== null;

      // Check drops_in metafield (matches Liquid: product.metafields.custom.drops_in)
      const dropsInRaw = productData.dropsIn || '';
      let dropsInDiff = 0;
      if (dropsInRaw) {
        const dropsInTs = new Date(dropsInRaw).getTime();
        const nowTs = Date.now();
        dropsInDiff = Math.floor((dropsInTs - nowTs) / 1000);
      }

      // Bag icon SVG (matches Liquid: {{ 'icon-bag-plus.svg' | inline_asset_content }})
      const bagPlusSvg = '<svg width="24" height="24" viewBox="4 6 16 16" xmlns="http://www.w3.org/2000/svg"><g stroke="#222" stroke-width="1.2" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="6.5" y="10.5" width="11" height="10" rx="1.5"/><path d="M8 10.5c0-1.8 2.0-2.8 4-2.8s4 1 4 2.8"/><line x1="12" y1="14" x2="12" y2="17"/><line x1="10.5" y1="15.5" x2="13.5" y2="15.5"/></g></svg>';

      // Build the close button SVG (reused in modals)
      const closeSvg = '<svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="xmark" class="svg-inline--fa fa-xmark" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="16" height="16"><path fill="currentColor" d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"></path></svg>';

      // Build price HTML for modal header
      const buildModalPrice = () => {
        if (!firstVariant) return '';
        const price = parseFloat(firstVariant.price?.amount || 0);
        const compareAt = firstVariant.compareAtPrice ? parseFloat(firstVariant.compareAtPrice.amount) : 0;
        if (compareAt > price && compareAt > 0) {
          const pct = Math.round(((compareAt - price) / compareAt) * 100);
          return `<div class="price price--on-sale"><span class="price-item price-item--regular"><span class="money">${this.formatMoney(price)}</span></span><span class="price-item price-item--sale" aria-hidden="true"><s><span class="money">${this.formatMoney(compareAt)}</span></s></span><span class="badge sale_badge_text">SAVE ${pct}%</span></div>`;
        }
        return `<div class="price"><span class="price-item price-item--regular"><span class="money">${this.formatMoney(price)}</span></span></div>`;
      };

      // Build dialog header (shared between cart and wishlist modals)
      const buildDialogHeader = (headerTitle) => {
        return `
          <div class="dialogHeader">
            <div class="dialogTitle"><div class="uppercase h4">${headerTitle}</div></div>
            <div class="dialogItem">
              <div class="dialogItemImage">
                <div class="media media--portrait"><img loading="lazy" width="128" height="160" src="${featuredImageUrl}" srcset="${featuredImageUrl}"></div>
              </div>
              <div>
                <div class="dialogItemHeader">
                  <p class="quickBadge"></p>
                  <p class="dialogItemTitle">${this.escapeHtml(titleParts[0])}</p>
                  ${titleParts.length > 1 ? `<p class="dialogItemSubtitle">${this.escapeHtml(titleParts.slice(1).join(' - '))}</p>` : ''}
                </div>
                ${buildModalPrice()}
                <p class="quickMessage"></p>
              </div>
            </div>
          </div>
        `;
      };

      // Zero-pad helper for countdown display
      const zeroPad = (n) => n < 10 ? '0' + n : '' + n;

      let html = '';

      // ── Branch order matches card-product-media.liquid exactly ──

      if (shopAt) {
        // 1. shop_at → retailer URL (matches supplement-info-add-to-cart.liquid logic)
        const shopAtUrl = shopAt.toLowerCase() === 'coles'
          ? 'https://www.coles.com.au/brands/muscle-nation-925608112'
          : 'https://www.woolworths.com.au/shop/search/products?searchTerm=Muscle%20Nation&autoFilter=true&originalSearchTerm=Muscle%20Nation&filter=Brand(Muscle%20Nation)';
        html = `<a href="${shopAtUrl}" target="_blank" rel="noopener noreferrer" class="card-product__oos-banner" data-shop="${this.escapeHtml(shopAt)}" aria-label="Shop at ${this.escapeHtml(shopAt)}">Shop at ${this.escapeHtml(shopAt)}</a>`;

      } else if (dropsInRaw && dropsInDiff > 0) {
        // 2. drops_in countdown (product.metafields.custom.drops_in is in the future)
        const diffD = Math.floor(dropsInDiff / 86400);
        const diffH = Math.floor((dropsInDiff % 86400) / 3600);
        const diffM = Math.floor((dropsInDiff % 3600) / 60);

        html = `
          <div class="card-product__drops-in">
            <div class="card-product__drops-in__title">Drops In</div>
            <div class="card-product__drops-in__units">
              <div class="card-product__drops-in__unit">
                <span class="card-product__drops-in__number">${zeroPad(diffD)}</span>
                <span class="card-product__drops-in__unit-label">Days</span>
              </div>
              <div class="card-product__drops-in__unit">
                <span class="card-product__drops-in__number">${zeroPad(diffH)}</span>
                <span class="card-product__drops-in__unit-label">Hrs</span>
              </div>
              <div class="card-product__drops-in__unit">
                <span class="card-product__drops-in__number">${zeroPad(diffM)}</span>
                <span class="card-product__drops-in__unit-label">Mins</span>
              </div>
            </div>
          </div>
        `;

      } else if (comingSoon) {
        // 3. coming_soon → wishlist button (banner style, opens variant modal for multi-variant)
        if (variants.length === 1) {
          html = `<button type="button" class="card-product__oos-banner backupWishlistButton backup-addToWishlist" data-variant-id="${variants[0]?.id || ''}">ADD TO WISHLIST</button>`;
        } else {
          html = `<button type="button" class="card-product__oos-banner backupWishlistButton backup-hasVariants" aria-haspopup="dialog">ADD TO WISHLIST</button>`;
        }

      } else if (noQuickAdd && isAvailable) {
        // 3b. no-quick-add → nothing on the card, the PDP owns the buy box
        // (matches card-product-media.liquid). Out of stock still falls through to BIS.
        html = '';

      } else {
        const tagsLower = tags.map(t => (typeof t === 'string' ? t : '').toLowerCase());
        // Accessories tagged as supplements use the normal quick-add, not SELECT FLAVOUR.
        const isAccessory = (productData.productType || '').toLowerCase().includes('accessories')
          || tagsLower.includes('accessories');
        const isSupplement = !isAccessory && tagsLower.some(t => t.includes('all products: supplements'));

        // Flavor-only supplement: no set- tag, no Size option → direct ATC
        const hasSetTag = tags.some(t => typeof t === 'string' && t.toLowerCase().startsWith('set-'));
        const hasSizeOption = (productData.options || []).some(opt => opt.name.toLowerCase() === 'size');
        const isFlavorOnlySupp = (isSupplement && !hasSetTag && !hasSizeOption) || productData.title.includes('PROTEIN WATER');

        if (isSupplement && isAvailable && !isFlavorOnlySupp) {
          // 4. In-stock supplement with size/set → SELECT FLAVOUR opens quick-add drawer
          html = `<button type="button" class="card-product__oos-banner card-product__select-flavour" aria-haspopup="dialog">SELECT FLAVOUR</button>`;

        } else if (isAvailable) {
          // 5. Available (non-supplement) → bag-icon button with cart modal for multi-variant
          const hasOverloadedVariants = variants.length > 14;

        if (isMultiVariant) {
          let cartActionsHtml = '';

          if (hasOverloadedVariants) {
            cartActionsHtml = `
              <div class="variantHitbntWrapper">
                <button type="button" class="hitButton sizeButtonVariant build-bundle-btn" data-product-handle="${handle}" onclick="window.location.href='/products/${handle}'">
                  <div>Build Bundle</div>
                </button>
              </div>
            `;
          } else if (hasSetTypes) {
            // Step 1: type buttons (each type = separate product via metafield)
            cartActionsHtml += `
              <div class="overload-step" data-step="1">
                <p class="overload-step__label">Select Type</p>
                <div class="overload-step__buttons">
                  ${setTypes.map(st => {
                    const hasAvail = st.product && st.product.variants.edges.some(v => v.node.availableForSale);
                    return `
                      <div class="variantHitbntWrapper">
                        <button type="button" class="hitButton sizeButtonVariant overload-opt1-btn${!hasAvail ? ' soldout' : ''}" data-option1="${this.escapeHtml(st.name)}" ${!hasAvail ? 'disabled="disabled"' : ''}>
                          <div>${this.escapeHtml(st.name)}</div>
                        </button>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;

            // Step 2: size buttons for each type's product
            setTypes.forEach(st => {
              if (!st.product) return;
              const stSizeIdx = st.product.sizeOptIdx || 0;
              const sizeButtons = st.product.variants.edges.map(v => {
                const variant = v.node;
                const vs = [variant.option1, variant.option2, variant.option3][stSizeIdx];
                const numericId = variant.id.replace('gid://shopify/ProductVariant/', '');
                return `
                  <div class="variantHitbntWrapper">
                    <button type="button" class="${variant.availableForSale ? 'backup-addToCart ' : ''}hitButton sizeButtonVariant" data-variant-id="${numericId}" ${!variant.availableForSale ? 'disabled="disabled"' : ''}>
                      <div>${this.escapeHtml(vs)}</div>
                    </button>
                  </div>
                `;
              }).join('');

              cartActionsHtml += `
                <div class="overload-step overload-step--hidden" data-step="2" data-for-option1="${this.escapeHtml(st.name)}">
                  <p class="overload-step__label">Select Size</p>
                  <div class="overload-step__buttons">
                    ${sizeButtons}
                  </div>
                </div>
              `;
            });
          } else if (hasTwoStep) {
            const primaryValues = options[primaryOptIdx].values;

            // Step 1: primary option buttons
            cartActionsHtml += `
              <div class="overload-step" data-step="1">
                <p class="overload-step__label">Select ${this.escapeHtml(primaryOptName)}</p>
                <div class="overload-step__buttons">
                  ${primaryValues.map(val => {
                    const hasAvail = variants.some(v => {
                      const vp = [v.node.option1, v.node.option2, v.node.option3][primaryOptIdx];
                      return vp === val && v.node.availableForSale;
                    });
                    return `
                      <div class="variantHitbntWrapper">
                        <button type="button" class="hitButton sizeButtonVariant overload-opt1-btn" data-option1="${this.escapeHtml(val)}" ${!hasAvail ? 'disabled="disabled"' : ''}>
                          <div>${this.escapeHtml(val)}</div>
                        </button>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;

            // Step 2: size buttons for each primary value
            primaryValues.forEach(val => {
              const sizeButtons = variants
                .filter(v => [v.node.option1, v.node.option2, v.node.option3][primaryOptIdx] === val)
                .map(v => {
                  const variant = v.node;
                  const vs = [variant.option1, variant.option2, variant.option3][sizeOptIdx];
                  const numericId = variant.id.replace('gid://shopify/ProductVariant/', '');
                  return `
                    <div class="variantHitbntWrapper">
                      <button type="button" class="${variant.availableForSale ? 'backup-addToCart ' : ''}hitButton sizeButtonVariant" data-variant-id="${numericId}" ${!variant.availableForSale ? 'disabled="disabled"' : ''}>
                        <div>${this.escapeHtml(vs)}</div>
                      </button>
                    </div>
                  `;
                }).join('');

              cartActionsHtml += `
                <div class="overload-step overload-step--hidden" data-step="2" data-for-option1="${this.escapeHtml(val)}">
                  <p class="overload-step__label">Select ${this.escapeHtml(sizeOptName)}</p>
                  <div class="overload-step__buttons">
                    ${sizeButtons}
                  </div>
                </div>
              `;
            });
          } else {
            cartActionsHtml = variants.map(v => {
              const variant = v.node;
              const numericId = variant.id.replace('gid://shopify/ProductVariant/', '');
              const disabled = !variant.availableForSale;
              return `
                <div class="variantHitbntWrapper">
                  <button type="button" class="${disabled ? '' : 'backup-addToCart '}hitButton sizeButtonVariant" data-variant-id="${numericId}" ${disabled ? 'disabled="disabled"' : ''}>
                    <div>${this.escapeHtml(variant.title)}</div>
                  </button>
                </div>
              `;
            }).join('');
          }

          html += `
            <div class="cartModal">
              <div class="cartDialog" role="dialog" aria-modal="true" tabindex="-1">
                <button type="button" class="fab dialogCloseButton">${closeSvg}</button>
                ${buildDialogHeader(hasOverloadedVariants ? 'Build Bundle' : 'Quick Add')}
                <div role="group" class="cartActions hasSizeVariant${hasSetTypes ? ' hasSetTypes' : ''}">
                  ${cartActionsHtml}
                </div>
              </div>
            </div>
          `;
        }

          // Bag-icon quick-add button (matches Liquid card-product-media.liquid)
          html += `
            <button
              type="button"
              aria-haspopup="dialog"
              aria-label="Add to cart"
              class="card-quick-add-icon ${isMultiVariant ? 'backup-hasVariants' : ''} ${isSingleVariant ? 'backup-addToCart' : ''}"
              ${isSingleVariant && firstVariant ? `data-variant-id="${firstVariant.id.replace('gid://shopify/ProductVariant/', '')}"` : ''}
            ><span class="card-quick-add-icon__svg">${bagPlusSvg}</span><span class="card-quick-add-icon__text">Quick Add</span></button>
          `;

        } else {
          // 6. Sold out (all products including supplements) → NOTIFY WHEN AVAILABLE BIS button
          const variantsForBis = variants.map(v => {
            const variant = v.node;
            const numericId = parseInt(variant.id.replace('gid://shopify/ProductVariant/', ''), 10);
            return {
              id: numericId,
              title: variant.title,
              available: variant.availableForSale,
              price: Math.round(parseFloat(variant.price?.amount || 0) * 100),
              compare_at_price: variant.compareAtPrice ? Math.round(parseFloat(variant.compareAtPrice.amount) * 100) : null
            };
          });
          // btoa throws on non-Latin1 chars (™, é) — encode as UTF-8 bytes first.
          const variantsBase64 = btoa(unescape(encodeURIComponent(JSON.stringify(variantsForBis))));

          html = `
            <div>
              <button
                type="button"
                class="card-product__oos-banner bis-link"
                aria-haspopup="dialog"
                data-product-id="${numericProductId}"
                data-product-title="${this.escapeHtml(productData.title)}"
                data-product-image="${featuredImageUrl}"
                data-product-url="${newUrl}"
                data-variants="${variantsBase64}"
              >NOTIFY WHEN AVAILABLE</button>
            </div>
          `;
        }
      }

      actionsContainer.innerHTML = html;

      // Keep data-flavor-only attribute in sync so isFlavorOnlySupplement() stays accurate after swatch changes
      if (typeof isFlavorOnlySupp !== 'undefined') {
        if (isFlavorOnlySupp) {
          actionsContainer.setAttribute('data-flavor-only', '');
        } else {
          actionsContainer.removeAttribute('data-flavor-only');
        }
      }

      // Liquid puts cartModal in .card-info (via backup-variantSelector), NOT inside [data-card-actions].
      // Move any newly-built cartModal out of actionsContainer to match the Liquid structure,
      // and remove stale cartModal from .card-info when switching to a product that doesn't need one.
      const cardInfo = this.querySelector('.card-info');
      const oldCartModal = this.querySelector('.card-info > .cartModal');
      const newCartModal = actionsContainer.querySelector('.cartModal');
      if (newCartModal) {
        if (oldCartModal) oldCartModal.remove();
        if (cardInfo) cardInfo.appendChild(newCartModal);
      } else if (oldCartModal) {
        oldCartModal.remove();
      }

      // Update wishlist buttons
      this.updateWishlistForSwatch(productData, handle);

      // Re-initialize variant and wishlist handling after DOM updates
      this.initializeVariantHandling();
      this.initializeWishlistButtonHandling();
    }

    updateWishlistForSwatch(productData, handle) {
      const variants = productData.variants?.edges || [];
      const firstVariant = variants[0]?.node;
      if (!firstVariant) return;

      const numericProductId = productData.id.replace('gid://shopify/Product/', '');
      const numericVariantId = firstVariant.id.replace('gid://shopify/ProductVariant/', '');
      const featuredImageUrl = productData.featuredImage?.url || '';

      // Update main wishlist button (heart icon in media area)
      const wishButton = this.querySelector('button.wishlistButton');
      if (wishButton) {
        if (variants.length === 1) {
          wishButton.classList.add('backup-addToWishlist');
          wishButton.dataset.variantId = numericVariantId;
          wishButton.dataset.productId = numericProductId;
          wishButton.dataset.handle = handle;
          wishButton.dataset.productTitle = productData.title;
          wishButton.dataset.image = featuredImageUrl;
          wishButton.dataset.price = Math.round(parseFloat(firstVariant.price.amount) * 100).toString();
          wishButton.dataset.variantTitle = firstVariant.title;
        } else {
          wishButton.classList.remove('backup-addToWishlist');
          wishButton.dataset.productId = numericProductId;
          wishButton.dataset.handle = handle;
          wishButton.dataset.productTitle = productData.title;
          wishButton.dataset.image = featuredImageUrl;
        }
        // Reset saved state (will be rechecked)
        wishButton.classList.remove('is-saved');
        const svg = wishButton.querySelector('svg.wishlist-heart');
        if (svg) svg.classList.remove('is-filled');
      }

      // Update backup wishlist button (in cartContainer for coming_soon)
      const backupWishBtn = this.querySelector('.backupWishlistButton');
      if (backupWishBtn) {
        if (variants.length === 1) {
          backupWishBtn.classList.add('backup-addToWishlist');
          backupWishBtn.classList.remove('backup-hasVariants');
          backupWishBtn.dataset.variantId = numericVariantId;
          backupWishBtn.dataset.productId = numericProductId;
          backupWishBtn.dataset.handle = handle;
          backupWishBtn.dataset.productTitle = productData.title;
          backupWishBtn.dataset.image = featuredImageUrl;
          backupWishBtn.dataset.price = Math.round(parseFloat(firstVariant.price.amount) * 100).toString();
          backupWishBtn.dataset.variantTitle = firstVariant.title;
        } else {
          backupWishBtn.classList.remove('backup-addToWishlist');
          backupWishBtn.classList.add('backup-hasVariants');
        }
      }

      // Update wishlist modal variant list (.wishModal)
      const wishModal = this.querySelector('.wishModal');
      if (wishModal) {
        // Update modal image
        const modalImage = wishModal.querySelector('.dialogItemImage img');
        if (modalImage) {
          modalImage.src = featuredImageUrl;
          modalImage.srcset = featuredImageUrl;
        }

        // Update modal title and subtitle
        const titleParts = productData.title.split(' - ');
        const modalTitle = wishModal.querySelector('.dialogItemTitle');
        const modalSubtitle = wishModal.querySelector('.dialogItemSubtitle');
        if (modalTitle) modalTitle.textContent = titleParts[0] || '';
        if (modalSubtitle) {
          if (titleParts.length > 1) {
            modalSubtitle.textContent = titleParts.slice(1).join(' - ');
            modalSubtitle.style.display = '';
          } else {
            modalSubtitle.textContent = '';
            modalSubtitle.style.display = 'none';
          }
        }

        // Rebuild variant buttons
        const variantList = wishModal.querySelector('.dialogList.addToWishlist');
        if (variantList) {
          variantList.innerHTML = variants.map(v => {
            const variant = v.node;
            const vNumericId = variant.id.replace('gid://shopify/ProductVariant/', '');
            const displayTitle = variant.title === 'Default Title' ? 'Add to wishlist' : this.escapeHtml(variant.title);
            return `
              <button
                class="dialogListItem backup-addToWishlist"
                data-variant-id="${vNumericId}"
                data-product-id="${numericProductId}"
                data-handle="${handle}"
                data-product-title="${this.escapeHtml(productData.title)}"
                data-image="${featuredImageUrl}"
                data-price="${Math.round(parseFloat(variant.price.amount) * 100)}"
                data-variant-title="${this.escapeHtml(variant.title)}"
              >${displayTitle}</button>
            `;
          }).join('');
        }
      }

      // Re-check wishlist state for the new product
      this.productData = this.getProductData();
      this.initializeWishlistHandling();
      this.fetchAndUpdateWishlistState();
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    // ==========================================
    // End Swatch Methods
    // ==========================================

    onClickAway(event) {
      const detailsElement = this.querySelector("details");
      const summaryElement = this.querySelector("summary");
      if (summaryElement && !summaryElement.contains(event.target)) {
        detailsElement?.removeAttribute("open");
      }
    }

    onChange(event) {
      this.querySelectorAll(".list-card-product__link").forEach((anchor) => {
        const url = new URL(anchor.href);
        url.searchParams.set("variant", event.currentTarget.value);
        anchor.href = url.toString();
      });

      this.productVariant = event.currentTarget.value;

      const listCardProduct = this.querySelector('.list-card-product');
      if (listCardProduct) {
        listCardProduct.classList.add("selected");
        const hideInput = listCardProduct.querySelector('.hide-input');
        if (hideInput) {
          hideInput.hidden = true;
        }
      }
    }

    onClick(event) {
      event.preventDefault();
      this.loadProduct(event.currentTarget.href);
    }

    async loadProduct(url) {
      try {
        const sectionName = "list-card-product";
        const response = await fetch(`${url}?sections=${sectionName}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();

        if (json[sectionName]) {
          this.innerHTML = this.getSectionInnerHTML(json[sectionName], sectionName);

          // Re-initialize all functionality. bindHostEvents() is deliberately NOT called
          // here — the host element survives this.innerHTML= and its listeners (bound once
          // in the constructor) are still attached; rebinding would stack duplicates.
          this.bindChildEvents();
          this.addAccessibilityAttributes();
          this.initializeVariantHandling();
          this.initializeWishlistHandling();
          this.initializeWishlistButtonHandling();
          this.initializeWishlistState();
          this.initializeSwatches();

          const listCardProduct = this.querySelector('.list-card-product');
          if (listCardProduct) {
            listCardProduct.classList.add("selected");
            const hideInput = listCardProduct.querySelector('.hide-input');
            if (hideInput) {
              hideInput.hidden = true;
            }

            const select = listCardProduct.querySelector('select');
            const option = select?.querySelector(`option[value="${this.productVariant}"]`);
            if (option) {
              option.selected = true;
            }
          }

          // Trigger polyfills and other scripts
          if (window.lazyloadPolyfill) {
            window.lazyloadPolyfill(this);
          }
          if (window.convertCurrency) {
            window.convertCurrency(this);
          }
        }
      } catch (error) {
        console.error('Error loading product:', error);
        this.showError('Failed to load product. Please try again.');
      }
    }

    getSectionInnerHTML(html, selector) {
      const DOM = new DOMParser().parseFromString(html, "text/html");
      return DOM.querySelector(selector)?.innerHTML || '';
    }
  });

  // Initialize backup wishlist button handlers for single variants
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.backup-wishListButton').forEach((variantButton) => {
      variantButton.addEventListener('click', function (e) {
        e.preventDefault();
        const variantId = this.getAttribute('data-variant-id');
        const dialogItem = document.querySelector(`.dialogListItem[data-variant-id="${variantId}"]`);
        if (dialogItem) {
          dialogItem.click();
        }
      });
    });
  });
}
